"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { formatCreditPrice } from "@/constants/creditPacks";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface CreditPack {
  credits: number;
  price: number;
  currency?: string;
  stripePriceId: string | undefined;
  badge?: "most-popular" | "best-value";
}

interface CreateCreditPackOptionsProps {
  packs: CreditPack[];
  onSelect: (
    credits: number,
    price: number,
    stripePriceId: string
  ) => void | Promise<void>;
  onSelectError?: (error: unknown) => void;
  disabled?: boolean;
  compact?: boolean;
}

const CREDIT_PACK_ACCENTS = {
  info: {
    button:
      "border-info/30 bg-info-muted/35 hover:border-info/50 hover:bg-info-muted/55",
    buy: "bg-info-muted text-info group-hover:bg-info group-hover:text-info-foreground",
    price: "text-info",
    focus: "focus-visible:ring-info/35",
  },
  warning: {
    button:
      "border-warning/35 bg-warning-muted/35 hover:border-warning/55 hover:bg-warning-muted/55",
    buy: "bg-warning-muted text-warning group-hover:bg-warning group-hover:text-warning-foreground",
    price: "text-warning",
    focus: "focus-visible:ring-warning/35",
  },
  success: {
    button:
      "border-success/35 bg-success-muted/35 hover:border-success/55 hover:bg-success-muted/55",
    buy: "bg-success-muted text-success group-hover:bg-success group-hover:text-success-foreground",
    price: "text-success",
    focus: "focus-visible:ring-success/35",
  },
} as const;

function getCreditPackAccent(pack: CreditPack) {
  if (pack.badge === "best-value") {
    return CREDIT_PACK_ACCENTS.success;
  }

  if (pack.badge === "most-popular") {
    return CREDIT_PACK_ACCENTS.warning;
  }

  return CREDIT_PACK_ACCENTS.info;
}

/**
 * Credit pack selection buttons matching the main Credits page top-up pattern.
 */
export function CreateCreditPackOptions({
  packs,
  onSelect,
  onSelectError,
  disabled = false,
  compact = false,
}: CreateCreditPackOptionsProps) {
  const selectionPendingRef = React.useRef(false);
  const [selectionPending, setSelectionPending] = React.useState(false);
  const handleSelect = async (pack: CreditPack) => {
    if (disabled || selectionPendingRef.current || !pack.stripePriceId) {
      return;
    }

    selectionPendingRef.current = true;
    setSelectionPending(true);
    try {
      const selection = onSelect(pack.credits, pack.price, pack.stripePriceId);
      if (selection) await selection;
    } catch (error) {
      onSelectError?.(error);
    } finally {
      selectionPendingRef.current = false;
      setSelectionPending(false);
    }
  };

  return (
    <div className={cn("grid", compact ? "gap-2 sm:gap-3" : "gap-3")}>
      {packs.map(pack => {
        const isUnavailable = !pack.stripePriceId;
        const perCredit = pack.credits > 0 ? pack.price / pack.credits : 0;
        const accent = getCreditPackAccent(pack);

        const badge =
          pack.badge === "most-popular"
            ? { text: "Recommended", variant: "warning" as const }
            : pack.badge === "best-value"
              ? { text: "Best value", variant: "success" as const }
              : null;

        return (
          <Button
            key={pack.credits}
            variant="outline"
            onClick={() => {
              void handleSelect(pack);
            }}
            aria-label={`Purchase ${pack.credits} credits for ${formatCreditPrice(pack.price, pack.currency)}`}
            disabled={disabled || selectionPending || isUnavailable}
            className={cn(
              "group h-auto w-full justify-start whitespace-normal rounded-lg p-0 text-left shadow-none outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-2",
              accent.button,
              accent.focus,
              isUnavailable && "opacity-60"
            )}
          >
            <span
              className={cn(
                "grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3",
                compact ? "px-3 py-2.5 sm:p-3" : "p-4"
              )}
            >
              <span
                className={cn("min-w-0", compact ? "space-y-1.5" : "space-y-2")}
              >
                <span
                  className={cn(
                    "block font-semibold leading-none text-foreground",
                    compact ? "text-base" : "text-xl"
                  )}
                >
                  {pack.credits} credits
                </span>
                <span className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {formatCreditPrice(perCredit, pack.currency)} per image
                  </span>
                  {badge && (
                    <Badge variant={badge.variant} size="sm">
                      {badge.text}
                    </Badge>
                  )}
                  {isUnavailable && (
                    <Badge variant="secondary" size="sm">
                      Unavailable
                    </Badge>
                  )}
                </span>
              </span>

              <span className="flex min-w-0 flex-col items-end gap-2">
                <span
                  className={cn(
                    "font-semibold leading-none",
                    compact ? "text-base" : "text-lg",
                    accent.price
                  )}
                >
                  {formatCreditPrice(pack.price, pack.currency)}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${accent.buy}`}
                >
                  {isUnavailable ? "Unavailable" : "Buy"}
                  {!isUnavailable && <ArrowRight className="size-3.5" />}
                </span>
              </span>
            </span>
          </Button>
        );
      })}
    </div>
  );
}
